import { useState } from 'react';
import { ShoppingListManager } from '@core/shopping';

interface PantryManagerProps {
  manager: ShoppingListManager;
  onUpdate: () => void;
}

export function PantryManager({ manager, onUpdate }: PantryManagerProps) {
  const [ingredient, setIngredient] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');

  const pantryItems = manager.getPantryItems();

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (!ingredient.trim() || !quantity.trim()) {
      return;
    }

    manager.addPantryItem(ingredient.trim(), parseFloat(quantity), unit.trim() || null);

    // Reset form
    setIngredient('');
    setQuantity('');
    setUnit('');

    onUpdate();
  };

  const handleRemove = (canonical: string) => {
    manager.removePantryItem(canonical);
    onUpdate();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Pantry Items</h2>
        <p className="text-sm text-gray-600 mb-6">
          Track what you already have at home. The shopping list will automatically subtract these items.
        </p>

        <form onSubmit={handleAdd} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ingredient
              </label>
              <input
                type="text"
                value={ingredient}
                onChange={(e) => setIngredient(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., flour"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <input
                type="number"
                step="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 5"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit (optional)
              </label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., cup"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Add to Pantry
          </button>
        </form>
      </div>

      {pantryItems.length > 0 ? (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Current Pantry ({pantryItems.length} items)
          </h3>

          <div className="space-y-3">
            {pantryItems.map((item) => (
              <div
                key={item.canonical}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{item.displayName}</div>
                  <div className="text-sm text-gray-600">
                    {item.quantity} {item.unit || 'units'} • {item.category}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(item.canonical)}
                  className="ml-4 text-gray-400 hover:text-red-600"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pantry items</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add items you already have at home using the form above.
          </p>
        </div>
      )}
    </div>
  );
}
